import { Box, easeInOutCubic, linear } from '@lemonade-hq/cdk';
import { COLORS } from '@lemonade-hq/tokens';
import { type Multiplier, inversedLerp, lerp } from '@lemonade-hq/ts-helpers';
import type React from 'react';
import { useMemo } from 'react';
import styled from 'styled-components';
import Tweener from '../GivebackShared/Tweener';
import Giveback2021Paragraph, { TOTAL_PARAGRAPH_DURATION, END_DURATION } from './Giveback2021Paragraph';
import Kaleidoscope from './Kaleidoscope';
import StoryTimeline from './StoryTimeline';
import { GIVEBACK_2021_TEXTS_WITH_SECTIONS } from './giveback2021Texts';
import {
  type Giveback2021Segment,
  pointsToRangeMaps,
  speedRangesToWeightedRanges,
  verifyContinuousRangedSegments,
  weightRangesToCoordinates,
} from './tweeningHelpers';
import usePrefersReducedMotion from './usePrefersReducedMotion';

import largeImageUrl from '@/static/assets/giveback_2021/gb2021l.png';
import mediumImageUrl from '@/static/assets/giveback_2021/gb2021m.png';
import smallImageUrl from '@/static/assets/giveback_2021/gb2021s.png';
import tinyImageUrl from '@/static/assets/giveback_2021/gb2021t.png';
import useValueAtRest from './useValueAtRest';

const PARAGRAPHS_TWEEN_PADDING = 0.004;
const STORY_WIDTH = '10em';
const STORY_TOP = '11.5em';
const SCALE_FROM = 0.96;
const BREATHING_DEBOUNCE = 100;

const KaleidoscopeContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  margin: 0 auto;
  position: fixed;
  top: 0;
`;

const TextContainer = styled.div`
  position: absolute;
  width: 100vw;
  height: 100vh;
  font-size: min(12px, max(6.5px, 0.8vw));
`;

const StyledStoryTimeline = styled(StoryTimeline)`
  top: 50%;
  left: 50%;
  position: absolute;
  transform: translate(-50%, calc(-50% - ${STORY_TOP}));
  width: ${STORY_WIDTH};
`;

export const paragraphAppearTween = (t: number): { opacity: number; display: string; transform: string } => {
  const result =
    t < PARAGRAPHS_TWEEN_PADDING
      ? lerp(0, 1, inversedLerp(0, PARAGRAPHS_TWEEN_PADDING, t))
      : t > 1 - PARAGRAPHS_TWEEN_PADDING
        ? lerp(1, 0, inversedLerp(1 - PARAGRAPHS_TWEEN_PADDING, 1, t))
        : 1;
  return {
    transform: `scale(${lerp(SCALE_FROM, 1, result)})`,
    opacity: result,
    display: t <= 0 || t >= 1 ? 'none' : 'block',
  };
};

const SLOW_SPEED: Multiplier = 0.325;
const SLOWER_SPEED: Multiplier = 0.65;
const HIGH_SPEED: Multiplier = 1.2;
const TIMELINE: Giveback2021Segment[] = [
  { range: [0.0, 0.035], speed: HIGH_SPEED, easing: linear },
  { range: [0.035, 0.055], speed: SLOWER_SPEED, easing: linear },
  { range: [0.055, 0.075], speed: SLOW_SPEED, easing: linear }, // environment
  { range: [0.075, 0.116], speed: HIGH_SPEED, easing: linear },
  { range: [0.116, 0.138], speed: SLOWER_SPEED, easing: linear },
  { range: [0.138, 0.156], speed: SLOW_SPEED, easing: linear }, // animal rights
  { range: [0.156, 0.216], speed: HIGH_SPEED, easing: linear },
  { range: [0.216, 0.236], speed: SLOWER_SPEED, easing: linear },
  { range: [0.236, 0.256], speed: SLOW_SPEED, easing: linear }, // poverty
  { range: [0.256, 0.341], speed: HIGH_SPEED, easing: linear },
  { range: [0.341, 0.361], speed: SLOWER_SPEED, easing: linear },
  { range: [0.361, 0.391], speed: SLOW_SPEED, easing: linear }, // education
  { range: [0.391, 0.521], speed: HIGH_SPEED, easing: linear },
  { range: [0.521, 0.541], speed: SLOWER_SPEED, easing: linear },
  { range: [0.541, 0.561], speed: SLOW_SPEED, easing: linear }, // civil rights
  { range: [0.561, 0.612], speed: HIGH_SPEED, easing: linear },
  { range: [0.612, 0.632], speed: SLOWER_SPEED, easing: linear },
  { range: [0.632, 0.652], speed: SLOW_SPEED, easing: linear }, // mental health
  { range: [0.652, 0.725], speed: HIGH_SPEED, easing: linear },
  { range: [0.725, 0.745], speed: SLOWER_SPEED, easing: linear },
  { range: [0.745, 0.765], speed: SLOW_SPEED, easing: linear }, // health
  { range: [0.765, 0.852], speed: HIGH_SPEED, easing: linear },
  { range: [0.852, 0.872], speed: SLOWER_SPEED, easing: linear },
  { range: [0.872, 0.892], speed: SLOW_SPEED, easing: linear }, // lgbtq+
  { range: [0.892, 1.0], speed: 0.8, easing: linear },
];

const ACCESSIBLE_SLOW_SPEED: Multiplier = 0.03; // very small so its weight become super high (which looks like it halts)
const ACCESSIBLE_HIGH_SPEED: Multiplier = 1000000; // very high so its weight become super small
const ACCESSIBLE_TIMELINE: Giveback2021Segment[] = [
  { range: [0.0, 0.054], speed: ACCESSIBLE_HIGH_SPEED, easing: linear },
  { range: [0.054, 0.05401], speed: ACCESSIBLE_SLOW_SPEED, easing: linear }, // environment
  { range: [0.05401, 0.1406], speed: ACCESSIBLE_HIGH_SPEED, easing: linear },
  { range: [0.1406, 0.14061], speed: ACCESSIBLE_SLOW_SPEED, easing: linear }, // animal rights
  { range: [0.14061, 0.2522], speed: ACCESSIBLE_HIGH_SPEED, easing: linear },
  { range: [0.2522, 0.25221], speed: ACCESSIBLE_SLOW_SPEED, easing: linear }, // poverty
  { range: [0.25221, 0.3931], speed: ACCESSIBLE_HIGH_SPEED, easing: linear },
  { range: [0.3931, 0.39311], speed: ACCESSIBLE_SLOW_SPEED, easing: linear }, // education
  { range: [0.39311, 0.5517], speed: ACCESSIBLE_HIGH_SPEED, easing: linear },
  { range: [0.5517, 0.55171], speed: ACCESSIBLE_SLOW_SPEED, easing: linear }, // civil rights
  { range: [0.55171, 0.6411], speed: ACCESSIBLE_HIGH_SPEED, easing: linear },
  { range: [0.6411, 0.64111], speed: ACCESSIBLE_SLOW_SPEED, easing: linear }, // mental health
  { range: [0.64111, 0.7582], speed: ACCESSIBLE_HIGH_SPEED, easing: linear },
  { range: [0.7582, 0.75821], speed: ACCESSIBLE_SLOW_SPEED, easing: linear }, // health
  { range: [0.75821, 0.8891], speed: ACCESSIBLE_HIGH_SPEED, easing: linear },
  { range: [0.8891, 0.88911], speed: ACCESSIBLE_SLOW_SPEED, easing: linear }, // lgbtq+
  { range: [0.88911, 1.0], speed: ACCESSIBLE_HIGH_SPEED, easing: linear },
];

if (!verifyContinuousRangedSegments(TIMELINE) || !verifyContinuousRangedSegments(ACCESSIBLE_TIMELINE)) {
  throw new Error('timeline definition is not continuous');
}

const Giveback2021KaleidoscopeContainer: React.FC<{
  t?: number;
  isFirefox: boolean;
  presentationMode: boolean;
}> = ({ t = 0, isFirefox, presentationMode }) => {
  const prefersReducedMotion = usePrefersReducedMotion();

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const rangeMaps = useMemo(() => {
    const weightedSegments = speedRangesToWeightedRanges(prefersReducedMotion ? ACCESSIBLE_TIMELINE : TIMELINE);
    const interpolationCoordinates = weightRangesToCoordinates(weightedSegments);
    return pointsToRangeMaps(interpolationCoordinates);
  }, []);

  const matchingRangeMapIndex = rangeMaps.findIndex(rangeMap => t <= rangeMap.x[1]);
  const correctedMatchingRangeMapIndex = matchingRangeMapIndex === -1 ? rangeMaps.length - 1 : matchingRangeMapIndex;
  const matchingRangeMap = rangeMaps[correctedMatchingRangeMapIndex];
  const matchingEasing = TIMELINE[correctedMatchingRangeMapIndex].easing;
  const tWithinRange = inversedLerp(matchingRangeMap.x[0], matchingRangeMap.x[1], t);
  const mappedTWithinRange = lerp(matchingRangeMap.y[0], matchingRangeMap.y[1], matchingEasing(tWithinRange));
  const mappedT = mappedTWithinRange;

  // breathing
  const breathingEnabled = useValueAtRest({
    changingValue: mappedT,
    duration: BREATHING_DEBOUNCE,
  });

  return (
    <KaleidoscopeContainer>
      <Kaleidoscope
        slices={14}
        sliceTransform={(t: number) => `rotate(${lerp(186.1, -174.2, t)}deg)`}
        imagesUrls={
          isFirefox
            ? [tinyImageUrl.src, tinyImageUrl.src, tinyImageUrl.src] // firefox has lousy performance when the rendered result is big...
            : [smallImageUrl.src, mediumImageUrl.src, largeImageUrl.src]
        }
        breathing={breathingEnabled && !prefersReducedMotion && mappedT > 0 && mappedT < 1}
        t={mappedT}
      />
      <TextContainer>
        {GIVEBACK_2021_TEXTS_WITH_SECTIONS.map(section => {
          return (
            <Tweener
              t={mappedT}
              tt={[section.range]}
              tween={paragraphAppearTween}
              ease={easeInOutCubic}
              key={section.title}
              render={({ style }: { style: React.CSSProperties }) => {
                const paused = style.opacity !== 1;

                return (
                  <Box width="100vw" height="100vh" style={{ display: 'none', ...style }}>
                    <StyledStoryTimeline
                      presentationMode={presentationMode}
                      sections={section.items.length}
                      paused={paused}
                      durationPerSection={TOTAL_PARAGRAPH_DURATION}
                      delayBeforeRestart={END_DURATION}
                      progressColor={section.timelineFlavor === 'light' ? COLORS.pink : COLORS.night}
                      foregroundColor={section.timelineFlavor === 'semi-dark' ? COLORS.white : COLORS.mercury}
                    />
                    {section.items.map((item, index) => {
                      return (
                        <Giveback2021Paragraph
                          key={section.title + item.subtitle}
                          title={item.title}
                          subtitle={item.subtitle}
                          organization={item.organization}
                          outOf={section.items.length}
                          index={index}
                          paused={paused}
                          titleColor={section.flavor === 'light' ? COLORS.white : COLORS.night}
                          orgColor={
                            section.flavor === 'light'
                              ? COLORS.white
                              : section.flavor === 'semi-dark'
                                ? COLORS.night
                                : COLORS.metal
                          }
                        />
                      );
                    })}
                  </Box>
                );
              }}
            />
          );
        })}
      </TextContainer>
    </KaleidoscopeContainer>
  );
};

export default Giveback2021KaleidoscopeContainer;
